import { useState, useRef, useMemo, useCallback, forwardRef, ForwardedRef } from "react"
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import { CloseHandler, DisclosureContext } from "../Contexts"
import Book from "./Book"
import { classNames } from "../../lib"
import { BookTitle, SectionItems, sections } from "../../../chuukese-books/src/books"

export const normalizeFilter = (s: string) =>
  s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()

export const focusRing = "focus:outline-none focus:ring-sky-500"
export const focusVisibleRing = "focus:outline-none focus-visible:ring-sky-500"

const Section = ({
  title,
  section,
  filter,
}: {
  title?: BookTitle
  section: SectionItems
  filter: string
}) => {
  const filtered = useMemo(
    () => section.filter(({ title }) => normalizeFilter(title).includes(normalizeFilter(filter))),
    [section, filter]
  )

  if (filtered.length === 0) {
    return null
  }

  return (
    <>
      {filtered.length > 0 && title && (
        <h3 className="h-8 text-center font-bold first-of-type:mt-0 last-of-type:mt-5">{title}</h3>
      )}
      {filtered.map(book => (
        <Book key={book.title} {...book} />
      ))}
    </>
  )
}

// eslint-disable-next-line no-empty-pattern
const BookNav = ({}, ref: ForwardedRef<HTMLDivElement>) => {
  const [filter, setFilter] = useState("")
  const [scrollUnder, setScrollUnder] = useState<boolean>(false)
  const [lastOpenDisclosure, setLastOpenDisclosure] = useState<HTMLButtonElement | null>(null)
  const closeRef = useRef<CloseHandler>(null)

  const checkPosition = useCallback(({ currentTarget }: React.UIEvent<HTMLDivElement>) => {
    setScrollUnder(currentTarget.scrollTop > 0)
  }, [])

  return (
    <DisclosureContext.Provider value={{ setLastOpenDisclosure, lastOpenDisclosure, closeRef }}>
      <div ref={ref}>
        <div
          className={classNames(
            "border-b-1 flex justify-center border-white -mt-px pb-4 transition-colors px-12",
            scrollUnder ? "shadow-[0px_1px_1px_0px_rgba(0,0,0,0.2)]" : "shadow-none"
          )}
        >
          <label className="relative mx-4 block w-full">
            <span className="sr-only">Filter by book</span>
            <span className="absolute inset-y-0 left-0 flex items-center pl-2">
              <MagnifyingGlassIcon className="size-5 text-slate-400" />
            </span>
            <input
              className={classNames(
                "block w-full rounded-2xl border border-slate-300 bg-white",
                "py-2 pl-9 pr-3 shadow-sm placeholder:italic placeholder:text-slate-400",
                focusRing,
                "focus:border-sky-500 focus:ring-1"
              )}
              value={filter}
              type="text"
              onChange={e => {
                setFilter(e.target.value)
              }}
              placeholder="Filter by book"
            />
          </label>
        </div>
        <div
          onScroll={checkPosition}
          className={classNames(
            "z-30 mx-auto overflow-y-auto overflow-x-hidden",
            "overscroll-contain h-[--book-nav-contents] sm:h-[--book-nav-contents-sm]"
          )}
        >
          {sections.map(s => (
            <Section {...s} key={s.title} filter={filter} />
          ))}
        </div>
      </div>
    </DisclosureContext.Provider>
  )
}

export default forwardRef(BookNav)
